import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Info, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ResponseStrategyRow } from '@/modules/response-strategies';
import { useResponseStrategies } from '@/hooks/use-response-strategies';
import { useResponseStrategyTemplates } from '@/hooks/use-response-strategy-templates';
import { usePermissions } from '@/hooks/use-permissions';
import type { ResponseStrategy } from '@/types';

export default function ResponseStrategies() {
	const navigate = useNavigate();
	const { can } = usePermissions();
	const canEdit = can('edit', 'response-strategy');
	const [bannerDismissed, setBannerDismissed] = useState(
		() => localStorage.getItem('response-strategy-banner-dismissed') === 'true'
	);
	const dismissBanner = () => {
		localStorage.setItem('response-strategy-banner-dismissed', 'true');
		setBannerDismissed(true);
	};

	const strategiesQuery = useResponseStrategies();
	const templatesQuery = useResponseStrategyTemplates();
	const isLoading = strategiesQuery.isLoading || templatesQuery.isLoading;
	const isError = strategiesQuery.isError;
	const strategies: ResponseStrategy[] = strategiesQuery.data?.strategies ?? [];
	// A strategy created/edited from a template carries `generatedBy: "template:<id>"` —
	// once that override exists, it IS the account's live view of that template (its own
	// intents/isActive), so drop the template's own row to avoid showing the same
	// template twice: once as the shared template, once as this account's copy of it.
	const adoptedTemplateIds = new Set(
		strategies
			.map((strategy) => strategy.generatedBy)
			.filter((generatedBy): generatedBy is string => Boolean(generatedBy?.startsWith('template:')))
			.map((generatedBy) => generatedBy.slice('template:'.length))
	);
	// Templates only carry a single `status` field (no draft/active model of their own — see
	// modules/response-strategies/types.ts); derive the two booleans so they render correctly
	// in the shared row component, which reads isDraft/isActive.
	const templates: ResponseStrategy[] = (templatesQuery.data ?? [])
		.filter((template) => !adoptedTemplateIds.has(template.id ?? template._id ?? ''))
		.map((template) => ({
			...template,
			accountId: 0,
			isTemplate: true
		}));
	const items: ResponseStrategy[] = [...strategies, ...templates];

	const handleCreate = () => navigate('/training/response-strategies/new');
	const handleRowClick = (strategy: ResponseStrategy) =>
		navigate(
			strategy.isTemplate
				? `/training/response-strategies/templates/${strategy.id}`
				: `/training/response-strategies/${strategy.id}`
		);

	return (
		<div className='flex flex-col h-full pt-4'>
			{/* Header */}
			<div className='flex justify-between items-center pb-4 px-4'>
				<div className='flex items-center gap-1.5'>
					<h1 className='text-base font-semibold'>Response Strategy</h1>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Info size={16} className='text-gray-800 cursor-default' />
							</TooltipTrigger>
							<TooltipContent side='bottom' sideOffset={8} variant='dark' className='max-w-72'>
								Describe how Kim should respond in a specific situation, and it will handle every matching customer
								conversation that way.
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
				{canEdit && (
					<Button className='h-8 gap-1.5 text-xs' onClick={handleCreate}>
						<Plus size={14} />
						Response strategy
					</Button>
				)}
			</div>

			{/* Info banner — only for accounts with no manually-created strategies yet */}
			{!isLoading && strategies.length === 0 && !bannerDismissed && (
				<div className='flex items-start gap-4 p-4 mx-4 mt-4 mb-4 rounded-2xl border border-gray-300 bg-blue-100'>
					<div className='flex-1 flex flex-col gap-1.5'>
						<div className='flex items-center gap-2'>
							<Info size={20} className='text-blue-foreground shrink-0' />
							<p className='text-sm font-medium text-blue-foreground'>Set Up Response Strategy</p>
						</div>
						<p className='text-xs text-gray-800'>
							Describe how Kim should respond in a specific situation, and it will handle every matching customer
							conversation that way. Start with a template or create your own strategy from scratch.
						</p>
					</div>
					<button
						type='button'
						onClick={dismissBanner}
						className='shrink-0 p-1 rounded hover:bg-ink/5'
						aria-label='Dismiss'
					>
						<X size={14} className='text-gray-800' />
					</button>
				</div>
			)}

			{/* Table */}
			{isLoading ? (
				<div className='flex flex-col gap-0 mt-4'>
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className='flex items-center gap-4 py-3.5 border-b border-gray-100 px-4'>
							<Skeleton className='h-4 w-48' />
							<Skeleton className='h-5 w-32 rounded-full' />
							<Skeleton className='h-5 w-10 ml-auto' />
						</div>
					))}
				</div>
			) : isError ? (
				<div className='flex flex-col items-center py-16 gap-3 mt-4'>
					<p className='text-sm text-red-500'>Failed to load response strategies</p>
					<Button variant='outline' size='sm' onClick={() => strategiesQuery.refetch()}>
						Try Again
					</Button>
				</div>
			) : (
				<div className='px-4 pb-4'>
				<Table className='table-auto'>
					<TableHeader>
						<TableRow>
							<TableHead className='w-2/5'>Name</TableHead>
							<TableHead>Intent</TableHead>
							<TableHead>
								<div className='flex items-center justify-end gap-3'>
									<div className='w-16' />
									<span className='h-4 w-px shrink-0 bg-gray-300' />
									<div className='flex items-center gap-[51px]'>
										<span className='flex w-14 justify-start'>Active</span>
										<span className='h-7 w-7 shrink-0' />
									</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{items.map((strategy) => (
							<ResponseStrategyRow key={strategy.id} strategy={strategy} onClick={handleRowClick} />
						))}
					</TableBody>
				</Table>
				</div>
			)}
		</div>
	);
}
